import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Listen,
  Prop,
  State,
  h,
} from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';
import {
  BitArray,
  SjclCipher,
  SjclElGamalPublicKey,
  SjclElGamalSecretKey,
  SjclKeyPair,
  cipher,
  codec,
  ecc,
  hash,
  mode,
  random,
} from 'sjcl-including-ecc';
import {
  AccessCodeEncrypted,
  AccessCodeEncryptedInfo,
  EcdheClientPubKey,
} from '../../class/class';
import { focusSignElement } from '../../utils/utils';

@Component({
  tag: 'rass-tin-input',
  styleUrl: 'rass-tin-input.scss',
  shadow: false,
})
export class RassTinInput {
  @Element() element!: HTMLStencilElement;
  @Prop() ecdheServerPubKey: AppModel.EcdhePubKey;
  @Prop() tinCodeMaxLength: number;
  @Prop() challengeInstructions: string;
  @Prop() summary: string;
  @State() tinCodeValue = '';
  @State() inputTinCodeLength = 0;
  @State() hasBlurred = false;
  @Event({ eventName: 'tinCode:change' }) tinCodeChangeEvent: EventEmitter;

  @Listen('tinCode:reset', {
    target: 'document',
  })
  handleTinCodeReset(): void {
    this.tinCodeValue = '';
    this.inputTinCodeLength = 0;
  }

  @Listen('keypress', {
    target: 'document',
  })
  handleKeyPress(e: KeyboardEvent): void {
    // Used to focus on the input whenever a [0-9] key is pressed, useful for situations where the user tabbed away from the tin input
    const numberRegex = new RegExp(/[0-9]/);
    if (numberRegex.test(e.key)) {
      focusSignElement('rass-tin-code-input', { preventScroll: true });
    }
  }

  componentDidLoad(): void {
    focusSignElement('rass-tin-code-input', { preventScroll: true });
  }

  handleInput(event: InputEvent): void {
    const inputElement: HTMLInputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value.replace(/[^0-9]/g, '');
    this.tinCodeValue = inputElement.value;
    this.inputTinCodeLength = this.tinCodeValue.length;
    if (this.inputTinCodeLength === this.tinCodeMaxLength) {
      this.emitTinCodeChangeEvent();
    }
  }

  private emitTinCodeChangeEvent(): void {
    this.tinCodeChangeEvent.emit(this.getAccessCodeEncryptedInfo());
  }

  private doSecure(
    serverPubKeyPointBits: BitArray,
    kEnc: BitArray,
    password: string,
    iv: string,
  ): string {
    random.addEntropy(
      String(serverPubKeyPointBits) + password + String(new Date().getTime()),
      password.length * 4 + 522,
      'page,millis,password',
    );
    const ivBits: BitArray = codec.base64.toBits(iv);
    const authData: BitArray = codec.hex.toBits('');
    const aesFunc: SjclCipher = new cipher.aes(kEnc);
    const passBytes: BitArray = codec.utf8String.toBits(password);
    const encPass: BitArray = mode.gcm.encrypt(
      aesFunc,
      passBytes,
      ivBits,
      authData,
      128,
    );
    return codec.base64.fromBits(encPass);
  }
  private getAccessCodeEncryptedInfo(): AccessCodeEncryptedInfo {
    const encodedInitVector: string = window.btoa(
      Math.random().toString(36).substring(2, 12),
    );

    const serverPubKeyPointBits: BitArray = codec.base64
      .toBits(this.ecdheServerPubKey.x)
      .concat(codec.base64.toBits(this.ecdheServerPubKey.y));

    const serverPubKey: SjclElGamalPublicKey = new ecc.elGamal.publicKey(
      ecc.curves.c256,
      serverPubKeyPointBits,
    );

    const clientKeys: SjclKeyPair<SjclElGamalPublicKey, SjclElGamalSecretKey> =
      ecc.elGamal.generateKeys(256, 1);

    const clientPubX: string = codec.base64.fromBits(clientKeys.pub.get().x);
    const clientPubY: string = codec.base64.fromBits(clientKeys.pub.get().y);

    const sharedKey: BitArray = clientKeys.sec.dhJavaEc(serverPubKey);
    const kEnc: BitArray = hash.sha256.hash(sharedKey);
    const tinCodeEncrypted: string = this.doSecure(
      serverPubKeyPointBits,
      kEnc,
      this.tinCodeValue,
      encodedInitVector,
    );

    const ecdheClientPubKey: AppModel.EcdhePubKey = new EcdheClientPubKey(
      this.ecdheServerPubKey.curve,
      clientPubX,
      clientPubY,
    );
    const accessCodeEncrypted: AppModel.Encrypted = new AccessCodeEncrypted(
      'AESGCM',
      encodedInitVector,
      tinCodeEncrypted,
    );
    return new AccessCodeEncryptedInfo(ecdheClientPubKey, accessCodeEncrypted);
  }

  onFocus(event: FocusEvent): void {
    const inputElement: HTMLInputElement = event.target as HTMLInputElement;
    setTimeout(
      () =>
        inputElement.setSelectionRange(
          this.inputTinCodeLength,
          this.inputTinCodeLength,
        ),
      0,
    );
  }

  onBlur(): void {
    this.hasBlurred = true;
  }

  get summaryPlainText(): string {
    const elem = document.createElement('div');
    elem.innerHTML = this.summary;
    elem.querySelectorAll('style').forEach((style) => style.remove());
    return elem.innerText;
  }

  render(): void {
    return (
      <Host class="section-tin-input">
        <div class="rass-tin-input-container rds-row rds-justify-content-center">
          <div id="aria-summary">{this.summaryPlainText}</div>
          <input
            id="rass-tin-code-input"
            type="text"
            class={{ password: true, 'has-blurred': this.hasBlurred }}
            autocomplete="off"
            inputmode="numeric"
            value={this.tinCodeValue}
            maxLength={this.tinCodeMaxLength}
            onFocus={(event: FocusEvent): void => this.onFocus(event)}
            onInput={(event: InputEvent): void => this.handleInput(event)}
            onBlur={(): void => this.onBlur()}
            aria-label={this.challengeInstructions}
            aria-describedby="aria-summary"
          />
          <div
            id="rass-tin-codes-container"
            class={`rass-tin-code-active-${this.inputTinCodeLength}`}
          >
            {Array.from({ length: this.tinCodeMaxLength }).map(
              (_elementInArray, index) =>
                (
                  <span
                    key={index}
                    class="circle"
                    id={`rass-tin-code-${index + 1}`}
                  ></span>
                ) as void,
            )}
          </div>
        </div>
      </Host>
    ) as void;
  }
}
